import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui';

const UserGuide = ({ activeSection: propActiveSection, onSectionChange }) => {
  const [activeSection, setActiveSection] = useState(propActiveSection || 'getting-started');

  useEffect(() => {
    if (propActiveSection) {
      setActiveSection(propActiveSection);
    }
  }, [propActiveSection]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (onSectionChange) {
      onSectionChange(section);
    }
  };

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀'
    },
    {
      id: 'feed',
      title: 'Social Feed',
      icon: '🔍'
    },
    {
      id: 'groups',
      title: 'Groups & Communities',
      icon: '👥'
    },
    {
      id: 'network',
      title: 'My Network',
      icon: '🤝'
    },
    {
      id: 'events',
      title: 'Events & Meetings',
      icon: '📅'
    },
    {
      id: 'resources',
      title: 'Learning Resources',
      icon: '📚'
    },
    {
      id: 'tenders',
      title: 'Tenders & Opportunities',
      icon: '📋'
    }
  ];

  const GettingStartedContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent mb-4">
          Welcome to Labamu Hub! 🎉
        </h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Your unified platform for merchant networking, collaboration, and trade opportunities. 
          Connect with fellow merchants, discover resources, and grow your business together.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👤</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Complete Your Profile</h3>
            <p className="text-gray-600 mb-4">
              Add your business information, profile picture, and contact details to help other merchants connect with you.
            </p>
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <strong>Tip:</strong> A complete profile gets 3x more connection requests!
            </div>
          </div>
        </Card>

        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔍</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Explore the Feed</h3>
            <p className="text-gray-600 mb-4">
              Discover posts from other merchants, share your experiences, and engage with the community through likes and comments.
            </p>
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <strong>Tip:</strong> Use hashtags to make your posts more discoverable!
            </div>
          </div>
        </Card>

        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👥</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Join Groups</h3>
            <p className="text-gray-600 mb-4">
              Connect with merchants in your industry or location. Join existing groups or create your own community.
            </p>
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <strong>Tip:</strong> Active group members get better business opportunities!
            </div>
          </div>
        </Card>

        <Card className="p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">📚</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-3">Access Resources</h3>
            <p className="text-gray-600 mb-4">
              Learn from expert articles, watch educational videos, and access tools to grow your business knowledge.
            </p>
            <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">
              <strong>Tip:</strong> Bookmark resources you want to reference later!
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-8 bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">🎯 Quick Start Checklist</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <span className="text-green-500">✅</span>
              <span>Create your account and verify email</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Complete your business profile</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Make your first post in the feed</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Join 2-3 relevant groups</span>
            </div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Connect with 5 other merchants</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Attend your first event</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Explore learning resources</span>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-gray-400">⏳</span>
              <span>Check out tender opportunities</span>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  const FeedContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">🔍 Social Feed Guide</h2>
        <p className="text-lg text-gray-600">
          Your central hub for community updates, discussions, and merchant insights.
        </p>
      </div>

      <div className="grid gap-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">✍️</span>
            Creating Posts
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">How to Create a Post:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Click the "Create Post" button at the top of the feed</li>
                <li>Choose your post type: Text, Photo, Link, or Poll</li>
                <li>Write your content - be authentic and engaging!</li>
                <li>Add relevant hashtags (e.g., #business #networking #tips)</li>
                <li>Attach photos or documents if needed</li>
                <li>Click "Post" to share with the community</li>
              </ol>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">💡 Best Practices:</h4>
              <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
                <li>Share valuable insights about your industry</li>
                <li>Ask questions to start discussions</li>
                <li>Use hashtags to increase visibility</li>
                <li>Keep posts professional but personable</li>
                <li>Include images when possible - they get more engagement</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">❤️</span>
            Engaging with Posts
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Available Actions:</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">❤️</span>
                  <div>
                    <div className="font-medium text-gray-800">Like</div>
                    <div className="text-sm text-gray-600">Show appreciation for good content</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">💬</span>
                  <div>
                    <div className="font-medium text-gray-800">Comment</div>
                    <div className="text-sm text-gray-600">Join the conversation with thoughtful replies</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">🔄</span>
                  <div>
                    <div className="font-medium text-gray-800">Share</div>
                    <div className="text-sm text-gray-600">Spread valuable content to your network</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">🚩</span>
                  <div>
                    <div className="font-medium text-gray-800">Flag</div>
                    <div className="text-sm text-gray-600">Report inappropriate content</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Engagement Tips:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>Comment with specific, helpful responses</li>
                <li>Share posts that would benefit your connections</li>
                <li>Like posts to support other merchants</li>
                <li>Use professional language in all interactions</li>
                <li>Follow up on conversations you start</li>
                <li>Report spam or inappropriate content</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🔧</span>
            Feed Features
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">📂 Filter Options</h4>
              <p className="text-sm text-gray-600">
                Filter posts by type (All, Text, Photos, Links, Polls) or by hashtags to find relevant content.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">🔔 Notifications</h4>
              <p className="text-sm text-gray-600">
                Get notified when someone likes, comments on, or shares your posts, or mentions you.
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">📱 Real-time Updates</h4>
              <p className="text-sm text-gray-600">
                The feed updates automatically with new posts and interactions as they happen.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const GroupsContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">👥 Groups & Communities</h2>
        <p className="text-lg text-gray-600">
          Connect with merchants in your industry, location, or interests. Build meaningful business relationships.
        </p>
      </div>

      <div className="grid gap-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🔍</span>
            Finding & Joining Groups
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Browse Groups:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Visit the Groups page from the sidebar</li>
                <li>Browse "All Groups" to see public communities</li>
                <li>Use filters to find groups by:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Industry (Tech, Retail, Manufacturing, etc.)</li>
                    <li>Location (Jakarta, Bandung, Surabaya, etc.)</li>
                    <li>Group type (Public, Private, Invite-only)</li>
                  </ul>
                </li>
                <li>Read group descriptions and member counts</li>
                <li>Click "Join Group" to become a member</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Group Types:</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-sm">🌍</span>
                  <div>
                    <div className="font-medium text-gray-800">Public Groups</div>
                    <div className="text-sm text-gray-600">Open to all members, visible to everyone</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm">🔒</span>
                  <div>
                    <div className="font-medium text-gray-800">Private Groups</div>
                    <div className="text-sm text-gray-600">Requires approval to join, content visible to members only</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-sm">📨</span>
                  <div>
                    <div className="font-medium text-gray-800">Invite-Only</div>
                    <div className="text-sm text-gray-600">Exclusive groups where you must be invited</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">💬</span>
            Group Chat & Communication
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Using Group Chat:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>In "My Groups" section, click the "Chat" button next to any group</li>
                <li>The group chat modal will open with recent messages</li>
                <li>Type your message in the input field at the bottom</li>
                <li>Press Enter or click Send to share your message</li>
                <li>View message history and see who's online</li>
                <li>Use the delete button to remove your own messages</li>
              </ol>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">💡 Chat Etiquette:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-700 text-sm">
                <li>Keep conversations relevant to the group's purpose</li>
                <li>Be respectful and professional in all messages</li>
                <li>Use @mentions to address specific members</li>
                <li>Share valuable resources and insights</li>
                <li>Avoid spam or excessive self-promotion</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">➕</span>
            Creating Your Own Group
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Steps to Create:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Click "Create Group" button on the Groups page</li>
                <li>Fill in group details:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Group name (clear and descriptive)</li>
                    <li>Description (purpose and guidelines)</li>
                    <li>Industry tags</li>
                    <li>Location</li>
                    <li>Privacy settings</li>
                  </ul>
                </li>
                <li>Set group rules and guidelines</li>
                <li>Choose moderators (optional)</li>
                <li>Submit for approval</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Management Tips:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>Create clear group guidelines and pin them</li>
                <li>Actively moderate discussions</li>
                <li>Encourage member participation</li>
                <li>Regular group activities and discussions</li>
                <li>Welcome new members personally</li>
                <li>Remove inactive or disruptive members</li>
                <li>Plan group meetups or events</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const NetworkContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">🤝 My Network</h2>
        <p className="text-lg text-gray-600">
          Build and manage your professional network of fellow merchants and business partners.
        </p>
      </div>

      <div className="grid gap-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🔍</span>
            Finding People to Connect
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Search Methods:</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">🔍</span>
                  <div>
                    <div className="font-medium text-gray-800">Find People Tab</div>
                    <div className="text-sm text-gray-600">Search by name, business, or location</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">👥</span>
                  <div>
                    <div className="font-medium text-gray-800">Group Members</div>
                    <div className="text-sm text-gray-600">Connect with people in your groups</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">💬</span>
                  <div>
                    <div className="font-medium text-gray-800">Post Interactions</div>
                    <div className="text-sm text-gray-600">Connect with people who engage with your posts</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">📅</span>
                  <div>
                    <div className="font-medium text-gray-800">Event Attendees</div>
                    <div className="text-sm text-gray-600">Meet people at events you attend</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Search Tips:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>Use specific keywords (company names, industries)</li>
                <li>Filter by location to find local merchants</li>
                <li>Look at mutual connections for introductions</li>
                <li>Check member profiles in relevant groups</li>
                <li>Search for people who posted interesting content</li>
                <li>Use advanced filters for better results</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">📨</span>
            Sending Connection Requests
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">How to Connect:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Visit the person's profile page</li>
                <li>Click the "Connect" button</li>
                <li>Write a personalized message explaining:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>How you found them</li>
                    <li>Why you want to connect</li>
                    <li>What value you can offer</li>
                    <li>Common interests or goals</li>
                  </ul>
                </li>
                <li>Send the request and wait for acceptance</li>
              </ol>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">✉️ Message Templates:</h4>
              <div className="space-y-3 text-sm">
                <div>
                  <strong className="text-blue-800">Group Connection:</strong>
                  <p className="text-blue-700 italic mt-1">
                    "Hi [Name], I noticed we're both members of [Group Name]. I'm also in [Your Industry] and would love to connect and share experiences."
                  </p>
                </div>
                <div>
                  <strong className="text-blue-800">Post Engagement:</strong>
                  <p className="text-blue-700 italic mt-1">
                    "Hi [Name], I really appreciated your recent post about [Topic]. As someone in [Your Industry], I'd love to connect and continue the conversation."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">📧</span>
            Inviting External People
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Invitation Process:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Go to the "Invite People" tab in My Network</li>
                <li>Click "Invite via Email" button</li>
                <li>Enter the person's email address</li>
                <li>Write a personal invitation message</li>
                <li>Include why they should join the platform</li>
                <li>Send the invitation</li>
                <li>Follow up if they don't respond within a week</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Who to Invite:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>Business partners and suppliers</li>
                <li>Colleagues from your industry</li>
                <li>Customers who might benefit</li>
                <li>Local business owners</li>
                <li>Professional contacts from events</li>
                <li>Members of other business networks</li>
              </ul>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <strong className="text-gray-800 text-sm">💡 Tip:</strong>
                <p className="text-gray-600 text-sm mt-1">
                  Quality over quantity - invite people who will actively participate!
                </p>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🤝</span>
            Managing Your Connections
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">👥 My Connections</h4>
              <p className="text-sm text-gray-600 mb-2">
                View all your accepted connections, organized by:
              </p>
              <ul className="text-xs text-gray-600 list-disc list-inside">
                <li>Recent connections</li>
                <li>Mutual connections</li>
                <li>Industry groups</li>
                <li>Location</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">📨 Pending Requests</h4>
              <p className="text-sm text-gray-600 mb-2">
                Manage incoming and outgoing requests:
              </p>
              <ul className="text-xs text-gray-600 list-disc list-inside">
                <li>Accept/decline requests</li>
                <li>View sent requests</li>
                <li>Cancel pending requests</li>
                <li>Add notes to connections</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">💬 Stay Connected</h4>
              <p className="text-sm text-gray-600 mb-2">
                Maintain relationships:
              </p>
              <ul className="text-xs text-gray-600 list-disc list-inside">
                <li>Like and comment on posts</li>
                <li>Share relevant content</li>
                <li>Send direct messages</li>
                <li>Collaborate on projects</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const EventsContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">📅 Events & Meetings</h2>
        <p className="text-lg text-gray-600">
          Discover networking events, workshops, and business meetings. Connect face-to-face with fellow merchants.
        </p>
      </div>

      <div className="grid gap-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🔍</span>
            Finding Events
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Browse Events:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Visit the Events page from the sidebar</li>
                <li>Use filters to find relevant events:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Date range (This week, This month, etc.)</li>
                    <li>Event type (Networking, Workshop, Conference)</li>
                    <li>Location (Online, Jakarta, Your city)</li>
                    <li>Industry focus</li>
                    <li>Price range (Free, Paid)</li>
                  </ul>
                </li>
                <li>Read event descriptions and details</li>
                <li>Check attendee count and who's going</li>
                <li>View organizer information</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Event Types:</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-sm">🤝</span>
                  <div>
                    <div className="font-medium text-gray-800">Networking Events</div>
                    <div className="text-sm text-gray-600">Meet other merchants, exchange contacts</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center text-sm">📚</span>
                  <div>
                    <div className="font-medium text-gray-800">Workshops</div>
                    <div className="text-sm text-gray-600">Learn new skills and business strategies</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center text-sm">🎤</span>
                  <div>
                    <div className="font-medium text-gray-800">Conferences</div>
                    <div className="text-sm text-gray-600">Large-scale industry events with speakers</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-sm">💻</span>
                  <div>
                    <div className="font-medium text-gray-800">Online Events</div>
                    <div className="text-sm text-gray-600">Virtual meetings and webinars</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🎫</span>
            Registering for Events
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Registration Process:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Click on an event to view full details</li>
                <li>Review event information:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Date, time, and duration</li>
                    <li>Location or online meeting link</li>
                    <li>Agenda and speakers</li>
                    <li>Requirements or prerequisites</li>
                    <li>Cost and payment options</li>
                  </ul>
                </li>
                <li>Click "Register" button</li>
                <li>Fill in registration form (if required)</li>
                <li>Complete payment (for paid events)</li>
                <li>Receive confirmation email with details</li>
                <li>Add event to your calendar</li>
              </ol>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">📱 After Registration:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-700 text-sm">
                <li>You'll receive a QR code for event check-in</li>
                <li>Event appears in your "My Events" section</li>
                <li>Get reminders via email and app notifications</li>
                <li>Access to event-specific chat or materials</li>
                <li>Ability to connect with other attendees</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🎯</span>
            Creating Your Own Events
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Event Creation Steps:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Click "Create Event" button</li>
                <li>Fill in event details:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Event title and description</li>
                    <li>Date, time, and duration</li>
                    <li>Location or online meeting setup</li>
                    <li>Event type and category</li>
                    <li>Maximum attendees</li>
                    <li>Pricing (free or paid)</li>
                  </ul>
                </li>
                <li>Set registration requirements</li>
                <li>Add event agenda and speakers</li>
                <li>Upload promotional images</li>
                <li>Submit for approval</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Event Management:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>Track registrations and attendance</li>
                <li>Send updates to registered attendees</li>
                <li>Manage event check-ins with QR codes</li>
                <li>Facilitate networking between attendees</li>
                <li>Collect feedback after the event</li>
                <li>Share event recordings or materials</li>
                <li>Export attendee list for follow-up</li>
              </ul>
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <strong className="text-gray-800 text-sm">💡 Success Tips:</strong>
                <ul className="text-gray-600 text-sm mt-2 list-disc list-inside">
                  <li>Start promoting 2-3 weeks in advance</li>
                  <li>Provide clear value proposition</li>
                  <li>Keep event focused and time-bound</li>
                  <li>Follow up with attendees afterward</li>
                </ul>
              </div>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">📋</span>
            Managing Your Events
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">📅 Upcoming Events</h4>
              <p className="text-sm text-blue-700">
                View events you've registered for, get reminders, and prepare for attendance.
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">🎤 My Events</h4>
              <p className="text-sm text-green-700">
                Manage events you're organizing, track registrations, and handle logistics.
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">📊 Event History</h4>
              <p className="text-sm text-purple-700">
                Review past events, access recordings, and maintain connections made.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const ResourcesContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">📚 Learning Resources</h2>
        <p className="text-lg text-gray-600">
          Access expert knowledge, educational content, and business tools to grow your merchant skills.
        </p>
      </div>

      <div className="grid gap-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🔍</span>
            Discovering Resources
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Resource Types:</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">📄</span>
                  <div>
                    <div className="font-medium text-gray-800">Articles</div>
                    <div className="text-sm text-gray-600">In-depth guides and expert insights</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">📹</span>
                  <div>
                    <div className="font-medium text-gray-800">Videos</div>
                    <div className="text-sm text-gray-600">Visual tutorials and presentations</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">📝</span>
                  <div>
                    <div className="font-medium text-gray-800">Blog Posts</div>
                    <div className="text-sm text-gray-600">Regular updates and industry news</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">📊</span>
                  <div>
                    <div className="font-medium text-gray-800">Tools & Templates</div>
                    <div className="text-sm text-gray-600">Practical business resources</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Finding Content:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Browse by categories:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Business Management</li>
                    <li>Marketing & Sales</li>
                    <li>Finance & Accounting</li>
                    <li>Legal & Compliance</li>
                    <li>Technology</li>
                    <li>Procurement</li>
                  </ul>
                </li>
                <li>Use search filters for specific topics</li>
                <li>Sort by popularity, date, or rating</li>
                <li>Check recommended resources</li>
                <li>Follow topics you're interested in</li>
              </ol>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">💡</span>
            Using Resources Effectively
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Learning Best Practices:</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                  <li>Set aside dedicated learning time</li>
                  <li>Take notes on key insights</li>
                  <li>Apply concepts to your business</li>
                  <li>Share learnings with your network</li>
                  <li>Join discussions about resources</li>
                  <li>Bookmark important resources</li>
                </ul>
                <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                  <li>Rate and review content you've used</li>
                  <li>Follow authors you find valuable</li>
                  <li>Create learning goals and track progress</li>
                  <li>Form study groups with other merchants</li>
                  <li>Practice new skills in real situations</li>
                  <li>Stay updated with latest content</li>
                </ul>
              </div>
            </div>
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">🎯 Creating Learning Plans:</h4>
              <ol className="list-decimal list-inside space-y-1 text-yellow-700 text-sm">
                <li>Identify skills gaps in your business</li>
                <li>Set specific learning objectives</li>
                <li>Choose 2-3 resources per topic</li>
                <li>Schedule regular learning sessions</li>
                <li>Track progress and apply knowledge</li>
                <li>Review and adjust plans monthly</li>
              </ol>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">❤️</span>
            Engaging with Content
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Available Actions:</h4>
              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">❤️</span>
                  <div>
                    <div className="font-medium text-gray-800">Like</div>
                    <div className="text-sm text-gray-600">Show appreciation for valuable content</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">💬</span>
                  <div>
                    <div className="font-medium text-gray-800">Comment</div>
                    <div className="text-sm text-gray-600">Share insights and ask questions</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">🔖</span>
                  <div>
                    <div className="font-medium text-gray-800">Bookmark</div>
                    <div className="text-sm text-gray-600">Save for later reference</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">⭐</span>
                  <div>
                    <div className="font-medium text-gray-800">Rate</div>
                    <div className="text-sm text-gray-600">Help others find quality content</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Sharing Knowledge:</h4>
              <ul className="list-disc list-inside space-y-2 text-gray-600 text-sm">
                <li>Write detailed reviews of resources you've used</li>
                <li>Share resources in relevant groups</li>
                <li>Recommend content to connections</li>
                <li>Create posts about key learnings</li>
                <li>Answer questions in comments</li>
                <li>Suggest improvements to authors</li>
                <li>Submit your own resources for publication</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">📊</span>
            Tracking Your Learning
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-800 mb-2">📖 Reading History</h4>
              <p className="text-sm text-blue-700">
                Track articles, videos, and posts you've consumed. See your learning progress over time.
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">🔖 My Bookmarks</h4>
              <p className="text-sm text-green-700">
                Access saved resources organized by categories and topics for easy reference.
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">🎯 Learning Goals</h4>
              <p className="text-sm text-purple-700">
                Set and track learning objectives, measure progress, and celebrate achievements.
              </p>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const TendersContent = () => (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">📋 Tenders & Opportunities</h2>
        <p className="text-lg text-gray-600">
          Discover business opportunities, tender announcements, and collaborative projects with other merchants.
        </p>
      </div>

      <div className="grid gap-8">
        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">🔍</span>
            Finding Opportunities
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Opportunity Types:</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">🏢</span>
                  <div>
                    <div className="font-medium text-gray-800">Government Tenders</div>
                    <div className="text-sm text-gray-600">Official procurement opportunities</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">🤝</span>
                  <div>
                    <div className="font-medium text-gray-800">Private Contracts</div>
                    <div className="text-sm text-gray-600">Business-to-business partnerships</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">👥</span>
                  <div>
                    <div className="font-medium text-gray-800">Group Buying</div>
                    <div className="text-sm text-gray-600">Collaborative purchasing for better prices</div>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <span className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">💼</span>
                  <div>
                    <div className="font-medium text-gray-800">Project Collaborations</div>
                    <div className="text-sm text-gray-600">Joint ventures and partnerships</div>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Search & Filter:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Use filters to find relevant opportunities:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Industry sector</li>
                    <li>Location</li>
                    <li>Contract value range</li>
                    <li>Deadline dates</li>
                    <li>Opportunity type</li>
                  </ul>
                </li>
                <li>Sort by deadline, value, or relevance</li>
                <li>Set up alerts for specific criteria</li>
                <li>Follow competitors to track market</li>
                <li>Save interesting opportunities</li>
              </ol>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">📝</span>
            Applying for Opportunities
          </h3>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Application Process:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Read opportunity details carefully</li>
                <li>Review requirements and qualifications</li>
                <li>Check deadlines and submission guidelines</li>
                <li>Prepare required documents:
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>Business registration certificates</li>
                    <li>Financial statements</li>
                    <li>Portfolio of previous work</li>
                    <li>Technical specifications</li>
                    <li>Pricing proposals</li>
                  </ul>
                </li>
                <li>Submit application before deadline</li>
                <li>Follow up on application status</li>
              </ol>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">💡 Success Tips:</h4>
              <ul className="list-disc list-inside space-y-1 text-green-700 text-sm">
                <li>Respond quickly to new opportunities</li>
                <li>Tailor proposals to specific requirements</li>
                <li>Highlight your unique value proposition</li>
                <li>Include client testimonials and case studies</li>
                <li>Ensure all documents are complete and professional</li>
                <li>Follow up professionally after submission</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">👥</span>
            Group Buying & Collaboration
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Joining Group Buying:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Browse group buying opportunities</li>
                <li>Review product/service details</li>
                <li>Check group size and target discount</li>
                <li>Join group if requirements met</li>
                <li>Coordinate with group organizer</li>
                <li>Make payment when minimum reached</li>
                <li>Receive bulk pricing benefits</li>
              </ol>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">Creating Group Buys:</h4>
              <ol className="list-decimal list-inside space-y-2 text-gray-600">
                <li>Identify products/services for bulk purchase</li>
                <li>Negotiate with suppliers for group rates</li>
                <li>Create group buying opportunity post</li>
                <li>Set minimum participants and targets</li>
                <li>Promote to relevant merchant groups</li>
                <li>Coordinate payments and logistics</li>
                <li>Distribute purchases to participants</li>
              </ol>
            </div>
          </div>
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="font-semibold text-blue-800 mb-2">🤝 Collaboration Benefits:</h4>
            <ul className="list-disc list-inside space-y-1 text-blue-700 text-sm">
              <li>Access to better pricing through volume discounts</li>
              <li>Reduced individual risk on large purchases</li>
              <li>Networking opportunities with other merchants</li>
              <li>Knowledge sharing about suppliers and products</li>
              <li>Collective bargaining power</li>
            </ul>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">📊</span>
            Managing Your Applications
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-800 mb-2">📝 Active Applications</h4>
              <p className="text-sm text-yellow-700">
                Track submitted applications, follow up on pending decisions, and manage required documents.
              </p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <h4 className="font-semibold text-green-800 mb-2">✅ Won Contracts</h4>
              <p className="text-sm text-green-700">
                Manage awarded contracts, track deliverables, and maintain client relationships.
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <h4 className="font-semibold text-purple-800 mb-2">📈 Performance Tracking</h4>
              <p className="text-sm text-purple-700">
                Analyze your success rate, identify improvement areas, and build your tender portfolio.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <span className="mr-3">⚠️</span>
            Important Considerations
          </h3>
          <div className="space-y-4">
            <div className="bg-red-50 border-l-4 border-red-200 p-4">
              <h4 className="font-semibold text-red-800 mb-2">🚨 Due Diligence:</h4>
              <ul className="list-disc list-inside space-y-1 text-red-700 text-sm">
                <li>Verify the legitimacy of tender opportunities</li>
                <li>Research the client organization thoroughly</li>
                <li>Check payment terms and contract conditions</li>
                <li>Ensure you can meet all requirements before applying</li>
                <li>Be wary of opportunities requiring upfront payments</li>
              </ul>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 mb-2">📋 Best Practices:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-sm">
                <li>Keep all business documents updated and ready</li>
                <li>Build relationships with potential partners</li>
                <li>Maintain a portfolio of successful projects</li>
                <li>Stay informed about industry trends and regulations</li>
                <li>Network actively to learn about upcoming opportunities</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'getting-started':
        return <GettingStartedContent />;
      case 'feed':
        return <FeedContent />;
      case 'groups':
        return <GroupsContent />;
      case 'network':
        return <NetworkContent />;
      case 'events':
        return <EventsContent />;
      case 'resources':
        return <ResourcesContent />;
      case 'tenders':
        return <TendersContent />;
      default:
        return <GettingStartedContent />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            📖 User Guide
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know to make the most of your Labamu Hub experience
          </p>
        </div>

        {/* Main Content - Full Width */}
        <Card className="p-8">
          {renderContent()}
        </Card>
      </div>
    </div>
  );
};

export default UserGuide;